import { useEffect, useState } from 'react';
import { Box, Button, Card, CardBody, CardHeader, DataTable, Menu, Spinner } from 'grommet';
import { CheckBox } from 'grommet';
import { Add, More } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { FooterNav, HeaderNav } from '/src/components';
import { AddRecipientModal } from '/src/components/admin/AddRecipientModal';
import AdminSideNav from '/src/components/admin/AdminSideNav';
import { EditRecipientModal } from '/src/components/admin/EditRecipientModal';
import { SendMoneyModal } from '/src/components/admin/SendMoneyModal';
import { DataTableHeader, DataTableItem, Text } from '/src/components/common';

interface StripeAccount {
  id: string;
  display_name: string;
  contact_email: string;
}

const mockAccounts: StripeAccount[] = [
  {
    id: 'acc_1',
    display_name: 'John Doe',
    contact_email: 'john.doe@example.com',
  },
  {
    id: 'acc_2',
    display_name: 'Jane Smith',
    contact_email: 'jane.smith@example.com',
  },
  {
    id: 'acc_3',
    display_name: 'Bob Johnson',
    contact_email: 'bob.johnson@example.com',
  },
];

const FuseAdminRemittance = observer(() => {
  const [accounts, setAccounts] = useState<StripeAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddRecipientModalVisible, setIsAddRecipientModalVisible] = useState(false);
  const [isSendMoneyModalVisible, setIsSendMoneyModalVisible] = useState(false);
  const [isEditRecipientModalVisible, setIsEditRecipientModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<StripeAccount | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setAccounts(mockAccounts);
    } catch (error) {
      console.error('Error fetching Stripe accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleSendMoney = (account: StripeAccount) => {
    setSelectedAccount(account);
    setIsSendMoneyModalVisible(true);
  };

  const handleCheckboxChange = (accountId: string) => {
    setSelectedAccounts((prevSelected) =>
      prevSelected.includes(accountId) ? prevSelected.filter((id) => id !== accountId) : [...prevSelected, accountId]
    );
  };

  const handleEditRecipient = (account: StripeAccount) => {
    setSelectedAccount(account);
    setIsEditRecipientModalVisible(true);
  };

  return (
    <Box height={{ min: '100vh' }} background="light" style={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderNav />
      <Box flex="grow" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        <AdminSideNav />
        <Box flex="grow" pad={{ horizontal: 'large', vertical: 'medium' }} overflow="auto">
          <Box flex={false}>
            <Box
              direction="row"
              align="center"
              justify="between"
              margin={{ vertical: 'medium' }}
              pad={{ horizontal: 'small' }}
            >
              <Text size="xlarge" weight="normal">
                Remittance{' '}
                <Text as="span" size="xlarge" color="status-critical" weight="normal">
                  Mock Data
                </Text>
              </Text>
            </Box>
            <Box align="center" justify="end" direction="row">
              <Button
                primary
                icon={<Add />}
                label="Add Recipient"
                onClick={() => setIsAddRecipientModalVisible(true)}
                style={{
                  borderRadius: '8px',
                  fontWeight: 'normal',
                }}
              />
              <Button
                primary
                icon={<Add />}
                label="Send Money"
                onClick={() => {}}
                style={{
                  borderRadius: '8px',
                  fontWeight: 'normal',
                  marginLeft: '8px',
                }}
                disabled={selectedAccounts.length === 0}
              />
            </Box>

            <Card margin={{ vertical: 'small' }} background="white" elevation="none" round="xsmall">
              <CardHeader
                pad={{ horizontal: 'medium', vertical: 'medium' }}
                border={{ side: 'bottom', color: 'light-4', size: '1px' }}
              >
                <Text size="large" weight="normal">
                  Stripe
                </Text>
              </CardHeader>
              <CardBody pad="none">
                {loading ? (
                  <Box align="center" justify="center" pad="large">
                    <Spinner size="medium" />
                  </Box>
                ) : (
                  <DataTable
                    pad={{ horizontal: '1.5rem' }}
                    columns={[
                      {
                        property: 'select',
                        header: (
                          <CheckBox
                            checked={selectedAccounts.length === accounts.length && accounts.length > 0}
                            onChange={() => {
                              if (selectedAccounts.length === accounts.length) {
                                setSelectedAccounts([]);
                              } else {
                                setSelectedAccounts(accounts.map((acc) => acc.id));
                              }
                            }}
                          />
                        ),
                        render: (datum: StripeAccount) => (
                          <CheckBox
                            checked={selectedAccounts.includes(datum.id)}
                            onChange={() => handleCheckboxChange(datum.id)}
                          />
                        ),
                        size: '0%',
                      },
                      {
                        property: 'display_name',
                        primary: true,
                        size: '30%',
                        header: <DataTableHeader toUpperCase>NAME</DataTableHeader>,
                        render: (datum: StripeAccount) => (
                          <Box pad={{ vertical: '1rem' }}>
                            <DataTableItem>{datum.display_name}</DataTableItem>
                          </Box>
                        ),
                      },
                      {
                        property: 'contact_email',
                        size: '20%',
                        header: <DataTableHeader toUpperCase>EMAIL</DataTableHeader>,
                        render: (datum: StripeAccount) => (
                          <Box pad={{ vertical: '1rem' }}>
                            <DataTableItem>{datum.contact_email}</DataTableItem>
                          </Box>
                        ),
                      },
                      {
                        property: 'id',
                        size: '20%',
                        header: <DataTableHeader toUpperCase>ID</DataTableHeader>,
                        render: (datum: StripeAccount) => (
                          <Box pad={{ vertical: '1rem' }}>
                            <DataTableItem>{datum.id}</DataTableItem>
                          </Box>
                        ),
                      },
                      {
                        property: 'actions',
                        size: '10%',
                        header: <DataTableHeader toUpperCase>ACTIONS</DataTableHeader>,
                        render: (datum: StripeAccount) => (
                          <Box round="xsmall" pad={{ vertical: '1rem' }}>
                            <Menu
                              icon={<More size="medium" />}
                              items={[
                                {
                                  label: 'Send Money',
                                  onClick: () => handleSendMoney(datum),
                                },
                                {
                                  label: 'Edit Email',
                                  onClick: () => handleEditRecipient(datum),
                                },
                              ]}
                              dropAlign={{ top: 'bottom', right: 'right' }}
                              dropProps={{
                                round: 'small',
                                elevation: 'medium',
                                background: 'white',
                                style: {
                                  minWidth: 180,
                                  borderRadius: 12,
                                  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                                  border: '1px solid #D2D8DF',
                                },
                              }}
                            />
                          </Box>
                        ),
                      },
                    ]}
                    data={accounts}
                    background={['light-6', 'white']}
                    border={{ color: 'light-2', side: 'bottom', size: 'small' }}
                  />
                )}
              </CardBody>
            </Card>
          </Box>
        </Box>
      </Box>
      <Box flex={false}>
        <FooterNav />
      </Box>
      <AddRecipientModal
        isVisible={isAddRecipientModalVisible}
        setIsVisible={setIsAddRecipientModalVisible}
        onRecipientAdded={fetchAccounts}
      />
      <SendMoneyModal
        isVisible={isSendMoneyModalVisible}
        setIsVisible={setIsSendMoneyModalVisible}
        selectedAccount={selectedAccount}
      />
      <EditRecipientModal
        isVisible={isEditRecipientModalVisible}
        setIsVisible={setIsEditRecipientModalVisible}
        selectedAccount={selectedAccount}
        onRecipientUpdated={fetchAccounts}
      />
    </Box>
  );
});

export default FuseAdminRemittance;
